import { useCallback, useEffect, useState } from 'react';
import { ArrowCounterClockwise, ArrowLeft, Trash, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { listDeletedVendors, restoreVendor } from '../lib/api';
import { Button } from '../components/ui/button';

const fmtDt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const TYPE_COLOR = {
  card: 'bg-indigo-100 text-indigo-800',
  gift_card: 'bg-purple-100 text-purple-800',
  recharge: 'bg-sky-100 text-sky-800',
  electricity: 'bg-amber-100 text-amber-800',
};

/**
 * Deleted Vendors / Recovery — owner/admin only page.
 * Lists vendors with `deleted_at` set (within last N days) and lets the admin
 * Restore them back to the active vendor list.
 *
 * The user explicitly requested this on Jun 2026:
 *   "delete ko permanent delete kar do....koi problem hoga to restore back
 *   se recovery kar lenge....power only admin k pas hona chahiye"
 */
export default function DeletedVendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [restoringId, setRestoringId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listDeletedVendors(days);
      setVendors(data?.vendors || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load deleted vendors');
      setVendors([]);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (v) => {
    const confirmMsg =
      `Vendor "${v.name}" ko restore karna hai?\n\n` +
      `Original email: ${v.original_email || v.deleted_email}\n` +
      `Cards archived: ${v.cards_archived}\n` +
      `Ledger archived: ${v.ledger_archived}\n\n` +
      `Restore karne par yeh vendor wapas active list me aa jayega.`;
    if (!window.confirm(confirmMsg)) return;
    setRestoringId(v.id);
    try {
      const { data } = await restoreVendor(v.id);
      toast.success(data?.message || `${v.name} restored`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally { setRestoringId(null); }
  };

  return (
    <div className="space-y-4" data-testid="deleted-vendors-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm">
            <a href="/gift-card-vendors" className="text-gray-600 hover:text-gray-900 inline-flex items-center gap-1" data-testid="back-to-vendors">
              <ArrowLeft size={14} /> Vendors
            </a>
            <span className="text-gray-400">/</span>
            <span className="font-semibold text-gray-900">Deleted Vendors (Recovery)</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-1 inline-flex items-center gap-2">
            <Trash size={22} weight="fill" className="text-red-500" />
            Deleted Vendors — Recovery
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Recently deleted vendors yahan dikhte hain. Galti se delete hua to <b>Restore</b> button se wapas la sakte ho.
            Admin/Owner ko hi access hai.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Last:</label>
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="h-9 text-sm border rounded px-2 bg-white"
            data-testid="recovery-days-select"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
          <Button onClick={load} variant="outline" data-testid="recovery-refresh-btn">Refresh</Button>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading…</div>
        ) : vendors.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Trash size={40} weight="duotone" className="mx-auto text-gray-300 mb-2" />
            <div className="text-sm">Last {days} days me koi vendor delete nahi hua. 🎉</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Original Email</th>
                <th className="px-3 py-2 text-right">Cards</th>
                <th className="px-3 py-2 text-right">Ledger</th>
                <th className="px-3 py-2 text-left">Deleted At</th>
                <th className="px-3 py-2 text-left">Deleted By</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map(v => (
                <tr key={v.id} className="border-t hover:bg-gray-50" data-testid={`deleted-vendor-${v.id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">{v.name}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${TYPE_COLOR[v.vendor_type] || 'bg-gray-100 text-gray-700'}`}>
                      {(v.vendor_type || '').replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {v.original_email || <span className="italic text-gray-400">unknown</span>}
                    {v.email_now_taken_by_other_vendor && (
                      <div className="text-[10px] text-amber-700 mt-0.5 inline-flex items-center gap-0.5">
                        <Warning size={10} /> Email ab kisi aur vendor ke pass hai
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{v.cards_archived}</td>
                  <td className="px-3 py-2 text-right">{v.ledger_archived}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{fmtDt(v.deleted_at)}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{v.deleted_by_name || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      onClick={() => handleRestore(v)}
                      disabled={restoringId === v.id || v.email_now_taken_by_other_vendor}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 disabled:opacity-50"
                      title={
                        v.email_now_taken_by_other_vendor
                          ? `Email ${v.original_email} ab kisi aur ke pass hai — pehle us vendor ko delete karo`
                          : 'Vendor ko wapas active list me lao'
                      }
                      data-testid={`restore-vendor-${v.id}`}
                    >
                      <ArrowCounterClockwise size={14} weight="bold" />
                      {restoringId === v.id ? 'Restoring…' : 'Restore'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
